/**
 * Substack serializer — HTML identity passthrough with subscribe /
 * paywall token substitution.
 *
 * Substack accepts HTML via post-by-email. Most of our canonical HTML
 * round-trips cleanly. Custom blocks get rewritten into Substack's
 * marker tokens:
 *
 *   <reach-subscribe>   → [subscribe]
 *   <reach-paywall>     → <!--paywall-->
 *
 * No length cap on Substack (their UI flags long posts but doesn't
 * reject).
 */

import { parseHtml, looksLikeHtml } from './common.js';

export function serializeForSubstack(htmlOrText) {
    const input = String(htmlOrText || '');
    if (input.trim() === '') {
        return { text: '', char_count: 0, warnings: [], errors: [] };
    }
    // Plain text — wrap in <p>; Substack renders it as a single paragraph.
    let html;
    if (!looksLikeHtml(input)) {
        html = '<p>' + input.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n\n+/g, '</p><p>').replace(/\n/g, '<br>') + '</p>';
    } else {
        html = input;
    }

    const root = parseHtml(html);
    const warnings = [];

    // Walk the tree and replace custom block elements with Substack tokens.
    // Accept BOTH `<reach-subscribe>` custom elements AND
    // `<div data-reach-subscribe>` divs (TipTap emits the latter so it
    // survives wp_kses_post on the server side). Same for paywall.
    const replacements = [];
    const stack = [root];
    while (stack.length) {
        const n = stack.pop();
        for (const child of Array.from(n.childNodes)) {
            if (child.nodeType !== 1) continue;
            const tag = child.tagName.toLowerCase();
            const isSubscribe = tag === 'reach-subscribe' || (child.hasAttribute && child.hasAttribute('data-reach-subscribe'));
            const isPaywall   = tag === 'reach-paywall' || (child.hasAttribute && child.hasAttribute('data-reach-paywall'));
            if (isSubscribe) {
                replacements.push([child, document.createTextNode('[subscribe]')]);
            } else if (isPaywall) {
                replacements.push([child, document.createComment('paywall')]);
            } else {
                stack.push(child);
            }
        }
    }
    for (const [oldNode, newNode] of replacements) {
        oldNode.parentNode.replaceChild(newNode, oldNode);
    }

    const out = root.innerHTML;
    // Plain-text "preview" length is the visible character count after
    // tag-strip — useful for the counter, even if Substack itself has
    // no hard cap.
    const visibleText = root.textContent.trim();
    return {
        text: out,
        char_count: visibleText.length,
        char_cap: 0, // unbounded — counter renders without "/ N" suffix
        warnings,
        errors: [],
    };
}
